import { IUser, Role } from '@/models';
import { FlagOrigin, IPersonFlag } from '@/models/flag';
import { isAnyTargetRole, isTargetRole } from '@/services';

export enum FlagPermission {
  VIEW = 'view',
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
  ASSIGN = 'assign',
  REMOVE = 'remove',
  EDIT_MANUAL_FLAGS = 'edit_manual_flags',
}

/**
 * Get all permissions for a user based on their role
 */
export const getUserFlagPermissions = (
  user: IUser | null,
): FlagPermission[] => {
  if (!user) return [];

  // ACCOUNT_ADMIN: All permissions
  if (isTargetRole(user, Role.ACCOUNT_ADMIN)) {
    return [
      FlagPermission.VIEW,
      FlagPermission.CREATE,
      FlagPermission.EDIT,
      FlagPermission.DELETE,
      FlagPermission.ASSIGN,
      FlagPermission.REMOVE,
    ];
  }

  // AGENCY_ADMIN: All permissions
  if (isTargetRole(user, Role.AGENCY_ADMIN)) {
    return [
      FlagPermission.VIEW,
      FlagPermission.CREATE,
      FlagPermission.EDIT,
      FlagPermission.DELETE,
      FlagPermission.ASSIGN,
      FlagPermission.REMOVE,
    ];
  }

  // RMS Cases Full access / RMS Cases View: police-employee flag permissions
  // (flag management is a police-employee action, not a case-write action).
  if (
    isAnyTargetRole(user, [Role.RMS_CASES_FULL_ACCESS, Role.RMS_CASES_VIEW])
  ) {
    return [
      FlagPermission.VIEW,
      FlagPermission.ASSIGN,
      FlagPermission.REMOVE,
      FlagPermission.EDIT_MANUAL_FLAGS,
    ];
  }

  // FIRST_RESPONDER: View, Assign, Remove
  if (isTargetRole(user, Role.FIRST_RESPONDER)) {
    return [FlagPermission.VIEW, FlagPermission.ASSIGN, FlagPermission.REMOVE];
  }

  // DISPATCHER: View only
  if (isTargetRole(user, Role.DISPATCHER)) {
    return [FlagPermission.VIEW];
  }

  // Default: No permissions
  return [];
};

/**
 * Check if user has a specific flag permission
 */
export const hasUserFlagPermission = (
  user: IUser | null,
  permission: FlagPermission,
): boolean => {
  const userPermissions = getUserFlagPermissions(user);
  return userPermissions.includes(permission);
};

/**
 * Check if user can edit a specific flag
 */
export const canUserEditFlag = (
  user: IUser | null,
  flag: IPersonFlag | null,
): boolean => {
  if (!user || !flag) return false;

  const permissions = getUserFlagPermissions(user);

  // Account and Agency admins can edit all flags
  if (permissions.includes(FlagPermission.EDIT)) {
    return true;
  }

  // Police can only edit manual flags
  if (
    permissions.includes(FlagPermission.EDIT_MANUAL_FLAGS) &&
    flag.origin === FlagOrigin.MANUAL
  ) {
    return true;
  }

  return false;
};

/**
 * Check if user can delete flags
 */
export const canUserDeleteFlag = (user: IUser | null): boolean => {
  return (
    hasUserFlagPermission(user, FlagPermission.DELETE) ||
    hasUserFlagPermission(user, FlagPermission.REMOVE)
  );
};

/**
 * Check if user can assign flags
 */
export const canUserAssignFlag = (user: IUser | null): boolean => {
  return hasUserFlagPermission(user, FlagPermission.ASSIGN);
};

/**
 * Check if user can create new flags (flag types)
 */
export const canUserCreateFlag = (user: IUser | null): boolean => {
  return hasUserFlagPermission(user, FlagPermission.CREATE);
};

/**
 * Check if user can view flags
 */
export const canUserViewFlag = (user: IUser | null): boolean => {
  return hasUserFlagPermission(user, FlagPermission.VIEW);
};
